import { useEffect, useState, type MouseEvent } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  Clock,
  Code,
  Copy,
  Download,
  Eye,
  Filter,
  Phone,
  RefreshCw,
  Search,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/ui";
import { cn } from "@/lib/utils";
import { ConversationLogsService } from "@/services/communication/ConversationLogsService";
import { AgentNameCell } from "./AgentNameCell";
import { ConversationLogDetailsSidebar } from "./ConversationLogDetailsSidebar";
import { useConversationLogs, useConversationLogStats } from "./hooks/useConversationLogs";
import { RefreshCountdown } from "./RefreshCountdown";

import type {
  ConversationLog,
  ConversationLogFilters,
  ConversationLogStatsPeriod,
} from "@/types/conversation-logs";

const STATS_FILTER_DEBOUNCE_MS = 300;

const STATS_PERIODS: { key: ConversationLogStatsPeriod; label: string; description: string }[] = [
  { key: "today", label: "Today", description: "Since midnight, your time" },
  { key: "last_7_days", label: "Last 7 days", description: "Today and the six days before" },
  { key: "all_time", label: "All time", description: "Every conversation ever logged" },
];

const STATUS_FILTER_LABELS: Record<string, string> = {
  pending: "Pending",
  processing: "Processing",
  recorded: "Recorded",
  completed: "Completed",
  failed: "Failed",
};

const TYPE_FILTER_LABELS: Record<string, string> = {
  regular: "Regular",
  setup: "Setup",
  mailbox: "Mailbox",
};

const ConversationLogsPage = () => {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState<ConversationLogFilters>({});
  const [page, setPage] = useState(1);
  const [selectedLog, setSelectedLog] = useState<ConversationLog | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [showRawDataModal, setShowRawDataModal] = useState(false);

  const {
    data: logsData,
    isLoading,
    isFetching,
    dataUpdatedAt,
    refetch: refetchLogs,
  } = useConversationLogs(filters, page, 50, !isDetailOpen);
  const statsFilters = useDebouncedValue(filters, STATS_FILTER_DEBOUNCE_MS);
  const {
    data: stats,
    isError: statsFailed,
    refetch: refetchStats,
  } = useConversationLogStats(statsFilters, !isDetailOpen);

  const appliedStatsFilters = [
    statsFilters.search?.trim() && `Search: "${statsFilters.search.trim()}"`,
    statsFilters.status && `Status: ${STATUS_FILTER_LABELS[statsFilters.status] ?? statsFilters.status}`,
    statsFilters.conversation_type &&
      `Type: ${TYPE_FILTER_LABELS[statsFilters.conversation_type] ?? statsFilters.conversation_type}`,
    statsFilters.connection_lost && "Connection lost",
  ].filter((label): label is string => Boolean(label));

  const handleRefresh = () => {
    refetchLogs();
    refetchStats();
  };

  // Auto-open conversation log by conversation ID from URL params
  useEffect(() => {
    const conversationId = searchParams.get("conversationId");

    if (conversationId && logsData?.data && !selectedLog && !isDetailOpen) {
      const matchingLog = logsData.data.find(
        (log) => log.conversation_id === parseInt(conversationId),
      );
      if (matchingLog) {
        setSelectedLog(matchingLog);
        setIsDetailOpen(true);
      }
    }
  }, [searchParams, logsData, selectedLog, isDetailOpen]);

  const handleExport = async () => {
    try {
      const csvContent = await ConversationLogsService.exportToCSV(filters);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `conversation-logs-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      toast.success("Conversation logs exported");
    } catch (error: any) {
      toast.error(`Export failed: ${error.message}`);
    }
  };

  const handleViewDetails = (log: ConversationLog) => {
    setSelectedLog(log);
    setIsDetailOpen(true);
  };

  const handleViewRawData = (log: ConversationLog) => {
    setSelectedLog(log);
    setShowRawDataModal(true);
  };

  // Controls inside a row act on their own instead of opening the details.
  const stopRowClick = (event: MouseEvent) => event.stopPropagation();

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    toast.success("ElevenLabs ID copied to clipboard");
  };

  const getStatusBadge = (status: string | null) => {
    const statusMap: Record<string, { icon: any; className: string; label: string }> = {
      pending: {
        icon: Clock,
        className: "bg-genie-background-muted text-genie-text-muted border-genie-border",
        label: "Pending",
      },
      processing: {
        icon: RefreshCw,
        className: "bg-yellow-100 text-yellow-700 border-yellow-200",
        label: "Processing",
      },
      recorded: {
        icon: CheckCircle,
        className: "bg-genie-primary-light text-genie-primary border-genie-primary/20",
        label: "Recorded",
      },
      completed: {
        icon: CheckCircle,
        className: "bg-green-100 text-green-700 border-green-200",
        label: "Completed",
      },
      failed: {
        icon: XCircle,
        className: "bg-genie-error-light text-genie-error border-genie-error/20",
        label: "Failed",
      },
    };

    const displayStatus = status || "pending";
    const config = statusMap[displayStatus] || statusMap.pending;
    const Icon = config.icon;

    return (
      <Badge className={cn(config.className, "inline-flex items-center gap-1 border w-fit")}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Phone className="h-8 w-8 text-genie-secondary" />
            <div>
              <h1 className="text-3xl font-bold text-genie-text-primary">Conversation Logs</h1>
              <p className="text-genie-text-secondary">
                Monitor all conversation activity and interactions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <RefreshCountdown
              isVisible={!isDetailOpen}
              dataUpdatedAt={dataUpdatedAt}
              onRefresh={handleRefresh}
              isRefreshing={isFetching}
            />
            <Button onClick={handleExport} variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold text-genie-text-primary">Conversation totals</h2>
              {appliedStatsFilters.length > 0 ? (
                <>
                  <span className="inline-flex items-center gap-1 text-sm text-genie-text-secondary">
                    <Filter className="h-3.5 w-3.5" />
                    counting only conversations matching
                  </span>
                  {appliedStatsFilters.map((label) => (
                    <Badge
                      key={label}
                      className="bg-genie-primary/10 text-genie-primary border border-genie-primary/20"
                    >
                      {label}
                    </Badge>
                  ))}
                </>
              ) : (
                <span className="text-sm text-genie-text-secondary">
                  all conversations, no filters applied
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {STATS_PERIODS.map(({ key, label, description }) => (
                <div
                  key={key}
                  className={cn(
                    "bg-white rounded-lg p-6 border",
                    appliedStatsFilters.length > 0 ? "border-genie-primary/40" : "border-genie-border",
                  )}
                >
                  <p className="text-sm font-medium text-genie-text-primary">{label}</p>
                  <p className="text-xs text-genie-text-muted">{description}</p>
                  {stats ? (
                    <>
                      <p className="text-3xl leading-9 font-bold text-genie-text-primary mt-3">
                        {stats[key].total}
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-sm">
                        <span className="inline-flex items-center gap-1 text-genie-primary">
                          <CheckCircle className="h-4 w-4" />
                          {stats[key].success} successful
                        </span>
                        <span className="inline-flex items-center gap-1 text-genie-error">
                          <XCircle className="h-4 w-4" />
                          {stats[key].failed} failed
                        </span>
                        <span className="inline-flex items-center gap-1 text-genie-accent-dark">
                          <Clock className="h-4 w-4" />
                          {stats[key].pending} pending
                        </span>
                      </div>
                    </>
                  ) : statsFailed ? (
                    <>
                      <p className="text-3xl leading-9 font-bold text-genie-text-muted mt-3">—</p>
                      <p className="mt-3 text-sm text-genie-text-muted">Totals could not be loaded</p>
                    </>
                  ) : (
                    <div aria-busy="true" aria-label={`Loading ${label} totals`}>
                      <Skeleton className="h-9 w-20 mt-3 bg-genie-background-muted" />
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
                        <Skeleton className="h-5 w-24 bg-genie-background-muted" />
                        <Skeleton className="h-5 w-16 bg-genie-background-muted" />
                        <Skeleton className="h-5 w-20 bg-genie-background-muted" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search agent name, email or conversation ID..."
                className="pl-10"
                value={filters.search || ""}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            <Select
              value={filters.status || "all"}
              onValueChange={(value) =>
                setFilters({ ...filters, status: value === "all" ? undefined : value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_FILTER_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.connection_lost ? "lost" : "all"}
              onValueChange={(value) =>
                setFilters({ ...filters, connection_lost: value === "lost" ? true : undefined })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Connection" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Connections</SelectItem>
                <SelectItem value="lost">Connection lost</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.conversation_type || "all"}
              onValueChange={(value) =>
                setFilters({ ...filters, conversation_type: value === "all" ? undefined : value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Conversation Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(TYPE_FILTER_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div></div>
            <Button variant="outline" onClick={() => setFilters({})} className="w-full">
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-lg border border-genie-border">
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-hidden">
            {/* Fixed column widths keep every column on screen at any width. */}
            <Table className="table-fixed [&_td]:px-3 [&_th]:px-3">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[24%]">Agent</TableHead>
                  <TableHead className="w-[22%]">Email</TableHead>
                  <TableHead className="w-[104px]">Type</TableHead>
                  <TableHead className="w-[136px]">Status</TableHead>
                  <TableHead>ElevenLabs ID</TableHead>
                  <TableHead className="w-[112px]">Created</TableHead>
                  <TableHead className="w-[104px]">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-genie-text-secondary">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                      Loading conversation logs...
                    </TableCell>
                  </TableRow>
                ) : logsData?.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-genie-text-muted">
                      No conversation logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logsData?.data.map((log) => (
                    <TableRow key={log.id} onClick={() => handleViewDetails(log)} className="cursor-pointer">
                      <TableCell className="font-medium text-genie-text-primary">
                        <AgentNameCell name={log.agent_name} logoUrl={log.agent_logo_url} />
                      </TableCell>
                      <TableCell className="text-genie-text-secondary">
                        <CopyableEmail email={log.email} />
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            "inline-flex items-center gap-1 border w-fit capitalize",
                            log.conversation_type === "setup"
                              ? "bg-genie-primary/10 text-genie-primary border-genie-primary/20"
                              : "bg-genie-background-muted text-genie-text-secondary border-genie-border",
                          )}
                        >
                          {log.conversation_type || "—"}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-xs text-genie-text-muted font-mono">
                        <div className="flex items-center gap-1 w-fit max-w-full" onClick={stopRowClick}>
                          <span className="truncate min-w-0" title={log.el_conv_id}>
                            {log.el_conv_id}
                          </span>
                          {log.el_conv_id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 flex-shrink-0 hover:bg-genie-background-muted"
                              onClick={() => handleCopyId(log.el_conv_id)}
                              title="Copy ID"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-genie-text-secondary whitespace-nowrap">
                        <div>{new Date(log.created_at).toLocaleDateString()}</div>
                        <div className="text-xs text-genie-text-muted">
                          {new Date(log.created_at).toLocaleTimeString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1" onClick={stopRowClick}>
                          <Button variant="ghost" size="sm" onClick={() => handleViewDetails(log)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewRawData(log)}
                            title="View raw ElevenLabs data"
                          >
                            <Code className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden">
            {isLoading ? (
              <div className="text-center py-8 text-genie-text-secondary">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                <p>Loading conversation logs...</p>
              </div>
            ) : logsData?.data.length === 0 ? (
              <div className="text-center py-8 text-genie-text-muted">
                No conversation logs found
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {logsData?.data.map((log) => (
                  <div
                    key={log.id}
                    onClick={() => handleViewDetails(log)}
                    className="border border-genie-border rounded-lg p-4 space-y-3 cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-genie-text-primary text-sm">
                          <AgentNameCell name={log.agent_name} logoUrl={log.agent_logo_url} />
                        </p>
                        <p className="text-xs text-genie-text-secondary break-words">
                          <CopyableEmail email={log.email} />
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0" onClick={stopRowClick}>
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(log)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewRawData(log)}
                          title="View raw ElevenLabs data"
                        >
                          <Code className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge
                        className={cn(
                          "inline-flex items-center gap-1 border capitalize text-xs",
                          log.conversation_type === "setup"
                            ? "bg-genie-primary/10 text-genie-primary border-genie-primary/20"
                            : "bg-genie-background-muted text-genie-text-secondary border-genie-border",
                        )}
                      >
                        {log.conversation_type || "—"}
                      </Badge>
                      {getStatusBadge(log.status)}
                    </div>

                    <div className="text-xs text-genie-text-secondary space-y-1">
                      <p>
                        <span className="text-genie-text-muted">Created:</span>{" "}
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                      <div className="flex items-center gap-2" onClick={stopRowClick}>
                        <span className="text-genie-text-muted">ID:</span>
                        <code className="text-genie-text-muted break-all flex-1">
                          {log.el_conv_id}
                        </code>
                        {log.el_conv_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 hover:bg-genie-background-muted flex-shrink-0"
                            onClick={() => handleCopyId(log.el_conv_id)}
                            title="Copy ID"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {logsData && logsData.count > 50 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="text-genie-text-secondary order-2 sm:order-1">
                Showing {(page - 1) * 50 + 1} to {Math.min(page * 50, logsData.count)} of{" "}
                {logsData.count} conversations
              </div>
              <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="flex-1 sm:flex-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page * 50 >= logsData.count}
                  className="flex-1 sm:flex-none"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Drawer */}
        <ConversationLogDetailsSidebar
          isOpen={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false);
            setShowRawDataModal(false);
          }}
          selectedLog={selectedLog}
          getStatusBadge={getStatusBadge}
          showRawDataModal={showRawDataModal}
          onShowRawDataModalChange={setShowRawDataModal}
        />
      </div>
    </div>
  );
};

export default ConversationLogsPage;
